<script>
  import { fade } from 'svelte/transition';
  import { onMount, onDestroy } from 'svelte';
  import { push, link, replace } from 'svelte-spa-router';
  import { get } from 'svelte/store';
  import { _ } from 'svelte-i18n';
  import fetchData from '../api.js';
  import Header from '../components/Header.svelte';
  import TopInfo from '../components/TopInfo.svelte';
  import Preloader from '../components/Preloader.svelte';
  import UserStore from '../stores/user';

  const WAVE_STATUS = {
    CLOSED: 'Closed',
  };
  const INVENTORY_SYNC_INTERVAL_MS = 60000;
  const IGNORED_WAVE_ITEM_CODE = '88840000';

  export let params;

  let waveId = '';
  let wave = null;
  let events = [];
  let bins = [];
  let isLoading = false;

  let skuInput = '';
  let binInput = '';
  let assignOrderInput = '';
  let assignBinInput = '';
  let assignOrderInputElement;
  let assignBinInputElement;
  let suggestion = null;
  let candidates = [];
  let selectedOrderId = '';
  let overrideReason = '';
  let externalOpenOrderCandidates = [];
  let selectedExternalOrders = {};
  let isAddingExternalOrders = false;
  let inventoryPopup = {
    open: false,
    orderId: '',
    item: null,
  };
  let inventoryMoveState = {
    visible: false,
    sku: '',
    fromLocation: '',
    toLocation: '',
    quantity: 1,
    sourceOptions: [],
    targetOptions: [],
  };
  let completionPopup = {
    open: false,
    orderId: '',
  };
  let completionPopupTimer = null;

  let message = '';
  let error = '';
  let selectedBinsByOrder = {};
  let createFlowStarted = false;
  let itemLocationHints = {};
  let itemInventoryHints = {};
  let warehouseLocations = [];
  let lastInventorySyncAt = 0;
  let isSyncingInventory = false;
  let isMovingInventory = false;

  function itemHintKey(orderId, sku) {
    return `${String(orderId || '').trim()}::${String(sku || '').trim()}`;
  }

  function itemHintsStorageKey(targetWaveId) {
    return `bins_wave_item_locations_${String(targetWaveId || '').trim()}`;
  }

  function itemInventoryStorageKey(targetWaveId) {
    return `bins_wave_item_inventory_${String(targetWaveId || '').trim()}`;
  }

  function parseWarehouseLocations(value) {
    return String(value || '')
      .split(',')
      .map((item) => item.trim())
      .filter(Boolean);
  }

  function shouldSyncLiveInventory() {
    return Date.now() - lastInventorySyncAt > INVENTORY_SYNC_INTERVAL_MS;
  }

  function normalizeInventoryByStore(inventory) {
    const map = {};
    (Array.isArray(inventory) ? inventory : []).forEach((entry) => {
      const storeId = String(entry?.store_id || '').trim();
      if (!storeId) {
        return;
      }

      const quantity = Number(entry?.quantity || 0);
      map[storeId] = (Number(map[storeId]) || 0) + Math.max(0, quantity);
    });

    return map;
  }

  function getOrderSkuInventory(orderId, sku) {
    return itemInventoryHints[itemHintKey(orderId, sku)] || {};
  }

  function mergeInventoryHints(nextHints) {
    itemInventoryHints = {
      ...itemInventoryHints,
      ...nextHints,
    };

    if (waveId) {
      sessionStorage.setItem(itemInventoryStorageKey(waveId), JSON.stringify(itemInventoryHints));
    }
  }

  function hasInventoryInConfiguredLocations(orderId, sku) {
    if (!warehouseLocations.length) {
      return true;
    }

    const inventoryMap = getOrderSkuInventory(orderId, sku);
    return warehouseLocations.some((location) => Number(inventoryMap[location] || 0) > 0);
  }

  function isItemOutOfStockInConfiguredLocations(orderId, item) {
    if (!warehouseLocations.length) {
      return false;
    }

    const itemInventory = item?.inventory_by_store || {};
    const inventoryMap = Object.keys(itemInventory).length
      ? itemInventory
      : getOrderSkuInventory(orderId, item?.sku);

    return !warehouseLocations.some((location) => Number(inventoryMap?.[location] || 0) > 0);
  }

  function itemInventoryRows(orderId, item) {
    const itemInventory = item?.inventory_by_store || {};
    const inventoryMap = Object.keys(itemInventory).length
      ? itemInventory
      : getOrderSkuInventory(orderId, item?.sku);

    return Object.keys(inventoryMap)
      .sort((a, b) => String(a).localeCompare(String(b)))
      .map((location) => ({
        location,
        quantity: Number(inventoryMap[location]) || 0,
      }));
  }

  function collectSkuInventoryByLocation(sku, orders = []) {
    const summary = {};

    (orders || []).forEach((order) => {
      const inventoryMap = getOrderSkuInventory(order?.order_id, sku);
      Object.keys(inventoryMap || {}).forEach((location) => {
        const qty = Number(inventoryMap[location] || 0);
        summary[location] = Math.max(Number(summary[location] || 0), qty);
      });
    });

    return summary;
  }

  function hideInventoryMovePrompt() {
    inventoryMoveState = {
      visible: false,
      sku: '',
      fromLocation: '',
      toLocation: '',
      quantity: 1,
      sourceOptions: [],
      targetOptions: [],
    };
  }

  function prepareInventoryMovePrompt(sku, orders = []) {
    if (!warehouseLocations.length) {
      hideInventoryMovePrompt();
      return;
    }

    const summary = collectSkuInventoryByLocation(sku, orders);
    const targetOptions = [...warehouseLocations];
    const sourceOptions = Object.keys(summary)
      .filter((location) => Number(summary[location] || 0) > 0 && !warehouseLocations.includes(location))
      .sort((a, b) => String(a).localeCompare(String(b)))
      .map((location) => ({
        location,
        quantity: Number(summary[location] || 0),
      }));

    if (!sourceOptions.length || !targetOptions.length) {
      hideInventoryMovePrompt();
      return;
    }

    inventoryMoveState = {
      visible: true,
      sku,
      fromLocation: sourceOptions[0].location,
      toLocation: targetOptions[0],
      quantity: 1,
      sourceOptions,
      targetOptions,
    };
  }

  async function moveInventoryBetweenWarehouses() {
    if (!inventoryMoveState.visible || isMovingInventory) {
      return;
    }

    const sku = String(inventoryMoveState.sku || '').trim();
    const fromLocation = String(inventoryMoveState.fromLocation || '').trim();
    const toLocation = String(inventoryMoveState.toLocation || '').trim();
    const quantity = Math.max(1, Number(inventoryMoveState.quantity) || 0);

    if (!sku || !fromLocation || !toLocation) {
      error = t('bins.sorter.errors.inventory_move_invalid');
      message = '';
      return;
    }

    isMovingInventory = true;
    try {
      const result = await request('bins_inventory_move', {
        wave_id: waveId,
        sku,
        from_location: fromLocation,
        to_location: toLocation,
        quantity,
      });

      if (!result?.success) {
        error = result?.error || t('bins.sorter.errors.inventory_move_failed');
        message = '';
        return;
      }

      hideInventoryMovePrompt();
      await refreshWave(true);
      message = t('bins.sorter.messages.inventory_moved_success');
      error = '';
    } catch {
      error = t('bins.sorter.errors.inventory_move_failed');
      message = '';
    } finally {
      isMovingInventory = false;
    }
  }

  function openInventoryPopup(orderId, item) {
    inventoryPopup = {
      open: true,
      orderId,
      item,
    };
  }

  function closeInventoryPopup() {
    inventoryPopup = {
      open: false,
      orderId: '',
      item: null,
    };
  }

  function closeCompletionPopup() {
    completionPopup = {
      open: false,
      orderId: '',
    };

    if (completionPopupTimer) {
      clearTimeout(completionPopupTimer);
      completionPopupTimer = null;
    }
  }

  function showCompletionPopup(orderId) {
    closeCompletionPopup();

    completionPopup = {
      open: true,
      orderId: String(orderId || '').trim(),
    };

    completionPopupTimer = setTimeout(() => {
      completionPopup = {
        open: false,
        orderId: '',
      };
      completionPopupTimer = null;
    }, 5000);
  }

  function onInventoryOverlayKeydown(event) {
    if (event.key === 'Escape' || event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      closeInventoryPopup();
    }
  }

  function t(key, values = {}) {
    return get(_)(key, { values });
  }

  function normalizeOrderScan(value) {
    return String(value || '').trim().replace(/^#/, '');
  }

  function normalizeProductScan(value) {
    return String(value || '').trim().toUpperCase();
  }

  function isIgnoredWaveItem(item) {
    const sku = String(item?.sku || '').trim();
    const barcode = String(item?.barcode || '').trim();
    return sku === IGNORED_WAVE_ITEM_CODE || barcode === IGNORED_WAVE_ITEM_CODE;
  }

  function externalOrderKey(candidate) {
    return `${String(candidate?.source_wave_id || '').trim()}::${String(candidate?.order_id || '').trim()}`;
  }

  async function request(action, payload = {}) {
    let formData = {
      token: $UserStore?.data,
      action,
      ...payload,
    };

    return fetchData(formData);
  }

  async function syncLiveInventoryForWaveOrders(orders = []) {
    if (!Array.isArray(orders) || !orders.length) {
      return;
    }

    const mergedHints = {};

    await Promise.all(
      orders.map(async (order) => {
        const identifiers = [String(order?.order_id || '').trim(), String(order?.order_name || '').trim()]
          .filter(Boolean)
          .filter((value, index, arr) => arr.indexOf(value) === index);

        for (const identifier of identifiers) {
          const response = await request('crm_get_shipment', {
            shipment_id: identifier,
            details: 1,
          });

          const shippingData = response?.data?.[0];
          if (!shippingData || !Array.isArray(shippingData.items)) {
            continue;
          }

          shippingData.items.forEach((item) => {
            const sku = String(item?.sku || '').trim();
            if (!sku) {
              return;
            }

            const inventoryMap = normalizeInventoryByStore(item?.inventory);
            if (Object.keys(inventoryMap).length) {
              mergedHints[itemHintKey(order.order_id, sku)] = inventoryMap;
            }
          });

          break;
        }
      }),
    );

    if (Object.keys(mergedHints).length) {
      mergeInventoryHints(mergedHints);
    }

    lastInventorySyncAt = Date.now();
  }

  async function refreshInventoryNow() {
    if (!wave || isSyncingInventory) {
      return;
    }

    isSyncingInventory = true;
    try {
      await refreshWave(true);
      message = t('bins.sorter.messages.inventory_refreshed');
      error = '';
    } catch {
      error = t('bins.sorter.errors.inventory_refresh_failed');
      message = '';
    } finally {
      isSyncingInventory = false;
    }
  }

  function normalizeWaveFromApi(rawWave) {
    if (!rawWave || !Array.isArray(rawWave.orders)) {
      return null;
    }

    const orders = rawWave.orders.map((order) => {
      const items = (Array.isArray(order.items) ? order.items : []).filter((item) => !isIgnoredWaveItem(item));
      const needs = {};
      const sorted = {};

      items.forEach((item) => {
        const sku = String(item?.sku || '').trim();
        if (!sku) {
          return;
        }

        const requiredQty = Number(item?.quantity || 0);
        const sortedQty = Number(item?.quantity_sorted || 0);
        needs[sku] = (Number(needs[sku]) || 0) + Math.max(0, Math.floor(requiredQty));
        sorted[sku] = (Number(sorted[sku]) || 0) + Math.max(0, Math.floor(sortedQty));
      });

      return {
        ...order,
        order_id: String(order?.order_id || ''),
        order_name: String(order?.order_name || ''),
        bin_code: String(order?.bin_code || '').trim(),
        sort_status: String(order?.sort_status || 'In-Sorting'),
        needs,
        sorted,
        items_details: buildItemDetails(items, order?.order_id),
      };
    });

    return {
      ...rawWave,
      wave_id: String(rawWave?.wave_id || ''),
      status: String(rawWave?.status || 'Draft'),
      type: String(rawWave?.type || 'full'),
      orders,
    };
  }

  function buildNeedsFromItems(items) {
    const needs = {};
    (items || []).forEach((item) => {
      if (isIgnoredWaveItem(item)) {
        return;
      }

      const sku = String(item?.sku || '').trim();
      const qty = Number(item?.quantity || 0);
      if (!sku || qty <= 0) {
        return;
      }

      needs[sku] = (Number(needs[sku]) || 0) + Math.floor(qty);
    });
    return needs;
  }

  function buildItemDetails(items, orderId = '') {
    const aggregated = {};

    (items || []).forEach((item) => {
      if (isIgnoredWaveItem(item)) {
        return;
      }

      const sku = String(item?.sku || '').trim();
      const qty = Number(item?.quantity || 0);
      if (!sku || qty <= 0) {
        return;
      }

      const hintLocation = itemLocationHints[itemHintKey(orderId, sku)] || '';
      const directLocation = String(item?.datalogics_location || '').trim();
      const datalogics_location = directLocation || hintLocation;

      if (!aggregated[sku]) {
        const inventoryByStore = normalizeInventoryByStore(item?.inventory);
        const hintInventory = getOrderSkuInventory(orderId, sku);

        aggregated[sku] = {
          sku,
          name: String(item?.name || '').trim(),
          quantity: 0,
          barcode: String(item?.barcode || '').trim(),
          datalogics_location,
          inventory_by_store: Object.keys(inventoryByStore).length ? inventoryByStore : hintInventory,
        };
      }

      aggregated[sku].quantity += Math.floor(qty);
      if (!aggregated[sku].name && item?.name) {
        aggregated[sku].name = String(item.name).trim();
      }
      if (!aggregated[sku].barcode && item?.barcode) {
        aggregated[sku].barcode = String(item.barcode).trim();
      }
      if (!aggregated[sku].datalogics_location && datalogics_location) {
        aggregated[sku].datalogics_location = datalogics_location;
      }

      const mergedInventory = {
        ...(aggregated[sku].inventory_by_store || {}),
        ...normalizeInventoryByStore(item?.inventory),
      };
      if (Object.keys(mergedInventory).length) {
        aggregated[sku].inventory_by_store = mergedInventory;
      }
    });

    return Object.values(aggregated).sort((a, b) => a.sku.localeCompare(b.sku));
  }

  async function createWaveFromFetchedOrders(shipmentsIds, tab) {
    const ids = String(shipmentsIds || '')
      .split(',')
      .map((id) => String(id || '').trim())
      .filter(Boolean);

    if (!ids.length) {
      error = 'No selected orders found.';
      return;
    }

    isLoading = true;
    error = '';
    message = '';

    try {
      const fetchedOrders = await Promise.all(ids.map(async (shipmentId) => {
        let formData = {
          action: 'crm_get_shipment',
          token: $UserStore.data,
          shipment_id: shipmentId,
          details: 1,
        };

        const response = await fetchData(formData);

        const shippingData = response?.data?.[0];
        if (!shippingData) {
          return null;
        }

        return {
          order_id: String(shippingData.order_id || shipmentId),
          order_name: String(shippingData.order_name || ''),
          created_at: shippingData.date || new Date().toISOString(),
          items: (shippingData.items || [])
            .map((item) => ({
              sku: String(item?.sku || '').trim(),
              name: String(item?.name || '').trim(),
              quantity: Math.max(0, Math.floor(Number(item?.quantity || 0))),
              barcode: String(item?.barcode || '').trim(),
              datalogics_location: String(item?.datalogics_location || '').trim(),
              inventory: Array.isArray(item?.inventory) ? item.inventory : [],
            }))
            .filter((item) => item.sku && item.quantity > 0 && !isIgnoredWaveItem(item)),
        };
      }));

      const waveOrders = fetchedOrders.filter((order) => order && Array.isArray(order.items) && order.items.length > 0);

      if (!waveOrders.length) {
        error = 'No valid order items found for wave creation.';
        return;
      }

      const generatedWaveId = `WAVE-${Date.now()}`;
      const createResult = await request('bins_wave_create', {
        wave_id: generatedWaveId,
        type: String(tab) === '2' ? 'shortages' : 'full',
        orders: waveOrders,
      });

      if (!createResult?.success) {
        error = createResult?.error || 'Failed to create wave.';
        return;
      }

      const nextWaveId = String(createResult.wave_id || generatedWaveId);
      const nextHints = {};
      const nextInventoryHints = {};
      waveOrders.forEach((order) => {
        (order.items || []).forEach((item) => {
          const location = String(item?.datalogics_location || '').trim();
          if (!location) {
            // keep going for inventory hints
          } else {
            nextHints[itemHintKey(order.order_id, item.sku)] = location;
          }

          const inventoryMap = normalizeInventoryByStore(item?.inventory);
          if (Object.keys(inventoryMap).length) {
            nextInventoryHints[itemHintKey(order.order_id, item.sku)] = inventoryMap;
          }
        });
      });

      itemLocationHints = nextHints;
      itemInventoryHints = nextInventoryHints;
      sessionStorage.setItem(itemHintsStorageKey(nextWaveId), JSON.stringify(nextHints));
      sessionStorage.setItem(itemInventoryStorageKey(nextWaveId), JSON.stringify(nextInventoryHints));

      const activateResult = await request('bins_wave_activate', {
        wave_id: nextWaveId,
      });
      if (!activateResult?.success) {
        error = activateResult?.error || 'Failed to activate wave.';
        return;
      }

      waveId = nextWaveId;
      await refreshWave();
      replace(`/bins/sorter/${encodeURIComponent(nextWaveId)}`);
    } finally {
      isLoading = false;
    }
  }

  async function refreshWave(forceInventorySync = false) {
    const [waveResult, binsResult, eventsResult, settingsResult] = await Promise.all([
      request('bins_wave_get', { wave_id: waveId }),
      request('bins_available', { wave_id: waveId }),
      request('bins_events_get', { wave_id: waveId, limit: 10 }),
      request('bins_settings_get'),
    ]);

    const rawWave = waveResult?.success ? waveResult.wave : null;
    if (rawWave?.wave_id) {
      const rawHints = sessionStorage.getItem(itemHintsStorageKey(rawWave.wave_id));
      if (rawHints) {
        try {
          itemLocationHints = JSON.parse(rawHints) || {};
        } catch {
          itemLocationHints = {};
        }
      } else {
        itemLocationHints = {};
      }

      const rawInventoryHints = sessionStorage.getItem(itemInventoryStorageKey(rawWave.wave_id));
      if (rawInventoryHints) {
        try {
          itemInventoryHints = JSON.parse(rawInventoryHints) || {};
        } catch {
          itemInventoryHints = {};
        }
      } else {
        itemInventoryHints = {};
      }
    }

    const settingsData = settingsResult?.success ? settingsResult?.settings : null;
    warehouseLocations = parseWarehouseLocations(settingsData?.warehouse_locations || '');

    let nextWave = rawWave ? normalizeWaveFromApi(rawWave) : null;

    if (nextWave && (forceInventorySync || shouldSyncLiveInventory())) {
      await syncLiveInventoryForWaveOrders(nextWave.orders || []);
      nextWave = normalizeWaveFromApi(rawWave);
    }

    wave = nextWave;
    bins = binsResult?.success && Array.isArray(binsResult.bins) ? binsResult.bins : [];
    events = eventsResult?.success && Array.isArray(eventsResult.events)
      ? eventsResult.events.map((ev) => ({
        time: ev.created_at,
        action: ev.event_type,
        sku: ev.sku || '',
        order_id: ev.order_id || '',
        bin_code: ev.bin_code || '',
        reason: ev.details || '',
      }))
      : [];

    if (wave && Array.isArray(wave.orders) && Array.isArray(bins)) {
      const connectedBinByOrder = bins.reduce((acc, bin) => {
        const orderId = String(bin?.order_id || '').trim();
        const binCode = String(bin?.bin_code || '').trim();
        if (!orderId || !binCode) {
          return acc;
        }

        const connected = Boolean(bin?.connected_order_in_wave);
        if (connected && !acc[orderId]) {
          acc[orderId] = binCode;
        }
        return acc;
      }, {});

      wave.orders = wave.orders.map((order) => {
        if (String(order?.bin_code || '').trim()) {
          return order;
        }

        const connected = connectedBinByOrder[String(order?.order_id || '').trim()] || '';
        if (!connected) {
          return order;
        }

        return {
          ...order,
          bin_code: connected,
        };
      });
    }

    if (wave) {
      selectedBinsByOrder = wave.orders.reduce((acc, order) => {
        acc[order.order_id] = order.bin_code || acc[order.order_id] || '';
        return acc;
      }, { ...selectedBinsByOrder });
    }

    if (wave && suggestion?.sku) {
      const sku = suggestion.sku;
      candidates = wave.orders.filter((order) => {
        const required = Number(order.needs[sku]) || 0;
        const sorted = Number(order.sorted[sku]) || 0;
        return required > sorted;
      });
    }
  }

  function recommendOrderForSkuLocal(sku) {
    const scannedCode = normalizeProductScan(sku);
    if (!wave || !scannedCode) {
      return { ok: false, error: t('bins.sorter.errors.sku_required') };
    }

    const normalizedCodeNoLeadingZeros = scannedCode.replace(/^0+/, '');
    const matchesScan = (value) => {
      const normalizedValue = normalizeProductScan(value);
      if (!normalizedValue) {
        return false;
      }

      if (normalizedValue === scannedCode) {
        return true;
      }

      if (!normalizedCodeNoLeadingZeros || normalizedCodeNoLeadingZeros === scannedCode) {
        return false;
      }

      return normalizedValue === normalizedCodeNoLeadingZeros;
    };

    const pendingSkuForOrder = (order) => {
      const skuRows = orderProgressRows(order).filter((row) => Number(row.required) > Number(row.sorted));
      for (const row of skuRows) {
        if (matchesScan(row.sku)) {
          return row.sku;
        }
      }

      const productRows = orderProducts(order);
      for (const item of productRows) {
        const itemSku = String(item?.sku || '').trim();
        if (!itemSku) {
          continue;
        }

        const required = Number(order?.needs?.[itemSku] || 0);
        const sorted = Number(order?.sorted?.[itemSku] || 0);
        if (required <= sorted) {
          continue;
        }

        if (matchesScan(item?.barcode)) {
          return itemSku;
        }
      }

      return '';
    };

    const baseMatching = wave.orders
      .filter((order) => {
        return Boolean(pendingSkuForOrder(order));
      })
      .sort((a, b) => new Date(a.created_at || 0).getTime() - new Date(b.created_at || 0).getTime());

    if (!baseMatching.length) {
      return { ok: false, error: 'This product does not belong to any order in this wave.' };
    }

    const skuByOrderId = baseMatching.reduce((acc, order) => {
      const matchedSku = pendingSkuForOrder(order);
      if (matchedSku) {
        acc[String(order.order_id)] = matchedSku;
      }
      return acc;
    }, {});

    const matching = warehouseLocations.length
      ? baseMatching.filter((order) => {
        const matchedSku = skuByOrderId[String(order.order_id)] || '';
        if (!matchedSku) {
          return false;
        }

        return hasInventoryInConfiguredLocations(order.order_id, matchedSku);
      })
      : baseMatching;

    if (!matching.length && warehouseLocations.length) {
      const suggestedSku = skuByOrderId[String(baseMatching[0]?.order_id || '')] || scannedCode;
      prepareInventoryMovePrompt(suggestedSku, baseMatching);
      return {
        ok: true,
        sku: suggestedSku,
        order_id: baseMatching[0].order_id,
        bin_code: baseMatching[0].bin_code,
        warning: t('bins.sorter.errors.no_inventory_locations', {
          sku: suggestedSku,
          locations: warehouseLocations.join(', '),
        }),
      };
    }

    const target = matching[0];
    const targetSku = skuByOrderId[String(target?.order_id || '')] || scannedCode;
    return {
      ok: true,
      sku: targetSku,
      order_id: target.order_id,
      bin_code: target.bin_code,
    };
  }

  function scanSku() {
    const sku = String(skuInput || '').trim();
    if (!sku) {
      error = t('bins.sorter.errors.sku_required');
      message = '';
      return;
    }

    const result = recommendOrderForSkuLocal(sku);
    if (!result.ok) {
      error = result.error;
      message = '';
      suggestion = null;
      candidates = [];
      selectedOrderId = '';
      externalOpenOrderCandidates = [];
      selectedExternalOrders = {};
      return;
    }

    suggestion = result;
    selectedOrderId = result.order_id;
    if (!result.warning) {
      hideInventoryMovePrompt();
    }
    error = '';
    message = result.bin_code
      ? t('bins.sorter.messages.insert_to_bin', { bin: result.bin_code })
      : t('bins.sorter.messages.scan_new_bin', { order: result.order_id });

    if (result.warning) {
      message = `${message} ${result.warning}`;
    }

    refreshWave();
    lookupOpenOrdersOutsideWave(sku, result.sku);
  }

  async function lookupOpenOrdersOutsideWave(scannedValue, matchedSku = '') {
    if (!waveId) {
      externalOpenOrderCandidates = [];
      selectedExternalOrders = {};
      return;
    }

    const result = await request('bins_open_orders_for_item', {
      wave_id: waveId,
      scan_value: String(scannedValue || '').trim(),
      matched_sku: String(matchedSku || '').trim(),
    });

    if (!result?.success || !Array.isArray(result?.orders)) {
      externalOpenOrderCandidates = [];
      selectedExternalOrders = {};
      return;
    }

    externalOpenOrderCandidates = result.orders;
    selectedExternalOrders = {};

    if (externalOpenOrderCandidates.length) {
      message = `${message ? `${message} ` : ''}This item is also needed by open orders in other waves.`;
    }
  }

  function toggleExternalOrderSelection(candidate, checked) {
    const key = externalOrderKey(candidate);
    selectedExternalOrders = {
      ...selectedExternalOrders,
      [key]: checked,
    };
  }

  async function addSelectedOpenOrdersToCurrentWave() {
    if (!waveId || isAddingExternalOrders || !externalOpenOrderCandidates.length) {
      return;
    }

    const selected = externalOpenOrderCandidates
      .filter((candidate) => selectedExternalOrders[externalOrderKey(candidate)])
      .map((candidate) => ({
        source_wave_id: String(candidate.source_wave_id || '').trim(),
        order_id: String(candidate.order_id || '').trim(),
      }))
      .filter((candidate) => candidate.source_wave_id && candidate.order_id);

    if (!selected.length) {
      error = 'Select at least one open order to add.';
      message = '';
      return;
    }

    isAddingExternalOrders = true;
    const result = await request('bins_move_open_orders_to_wave', {
      wave_id: waveId,
      source_orders: selected,
    });

    if (!result?.success) {
      error = result?.error || 'Failed to add selected orders to this wave.';
      message = '';
      isAddingExternalOrders = false;
      return;
    }

    error = '';
    const movedCount = Array.isArray(result?.moved_orders) ? result.moved_orders.length : selected.length;
    message = movedCount === 1
      ? 'Added 1 open order to this wave.'
      : `Added ${movedCount} open orders to this wave.`;

    externalOpenOrderCandidates = [];
    selectedExternalOrders = {};
    await refreshWave();
    isAddingExternalOrders = false;
  }

  async function commitSort() {
    if (!suggestion?.sku) {
      error = t('bins.sorter.errors.scan_sku_first');
      message = '';
      return;
    }

    const targetOrderId = selectedOrderId || suggestion.order_id;
    const targetOrder = wave?.orders?.find((order) => String(order.order_id) === String(targetOrderId));
    const wasOrderFullyScanned = targetOrder ? isOrderFullyScanned(targetOrder) : false;
    const scannedBin = String(binInput || '').trim().toUpperCase();
    const assignedBin = String(targetOrder?.bin_code || '').trim().toUpperCase();
    const binToUse = scannedBin || assignedBin;

    if (!targetOrder) {
      error = t('bins.sorter.errors.order_not_found');
      message = '';
      return;
    }

    if (!hasInventoryInConfiguredLocations(targetOrder.order_id, suggestion.sku)) {
      const ordersForSku = (wave?.orders || []).filter((order) => Number(order?.needs?.[suggestion.sku] || 0) > 0);
      prepareInventoryMovePrompt(suggestion.sku, ordersForSku);
      const inventoryWarning = t('bins.sorter.errors.no_inventory_locations', {
        sku: suggestion.sku,
        locations: warehouseLocations.join(', '),
      });
      message = `${message ? `${message} ` : ''}${inventoryWarning}`;
      error = '';
    }

    if (!binToUse) {
      error = t('bins.sorter.errors.bin_required');
      message = '';
      return;
    }

    if (assignedBin && assignedBin !== binToUse) {
      error = `Wrong bin. Order ${targetOrder.order_id} is assigned to ${targetOrder.bin_code}.`;
      message = '';
      return;
    }

    if (!targetOrder.bin_code) {
      const assignResult = await request('bins_assign', {
        wave_id: waveId,
        order_id: targetOrder.order_id,
        bin_code: binToUse,
        assignment_method: 'scan',
      });

      if (!assignResult?.success) {
        error = assignResult?.error || 'Failed to assign bin.';
        message = '';
        await refreshWave();
        return;
      }
    }

    const result = await request('bins_sort_sku', {
      wave_id: waveId,
      order_id: targetOrder.order_id,
      sku: suggestion.sku,
      bin_code: binToUse,
      quantity: 1,
    });

    if (!result?.success) {
      error = result?.error || 'Failed to sort SKU.';
      message = '';
      return;
    }

    error = '';
    message = result?.message || 'SKU sorted successfully.';
    hideInventoryMovePrompt();

    skuInput = '';
    binInput = '';
    suggestion = null;
    selectedOrderId = '';
    overrideReason = '';
    candidates = [];
    await refreshWave();

    const updatedOrder = wave?.orders?.find((order) => String(order.order_id) === String(targetOrderId));
    if (!wasOrderFullyScanned && updatedOrder && isOrderFullyScanned(updatedOrder)) {
      showCompletionPopup(targetOrderId);
    }
  }

  async function closeCurrentWave() {
    const result = await request('bins_wave_close', { wave_id: waveId });
    if (!result?.success) {
      error = result?.error || 'Failed to close wave.';
      message = '';
      return;
    }

    error = '';
    message = t('bins.sorter.messages.wave_closed_manual');
    await refreshWave();
  }

  function isOrderFullyScanned(order) {
    const rows = orderProgressRows(order);
    if (!rows.length) {
      return false;
    }

    return rows.every((row) => Number(row.sorted) >= Number(row.required));
  }

  async function releaseOrderBin(order) {
    const orderId = String(order?.order_id || '').trim();
    if (!orderId) {
      error = 'Order not found.';
      message = '';
      return;
    }

    const result = await request('bins_assignment_release', {
      wave_id: waveId,
      order_id: orderId,
    });

    if (!result?.success) {
      error = result?.error || t('bins.sorter.errors.release_bin_failed');
      message = '';
      await refreshWave();
      return;
    }

    error = '';
    message = result?.message || t('bins.sorter.messages.release_bin_success', { order: orderId });
    await refreshWave();
  }

  function availableBinsForOrder(order) {
    return bins.filter((bin) => {
      const code = String(bin.bin_code || '').trim();
      if (!code) {
        return false;
      }

      if (order.bin_code === code) {
        return true;
      }

      return bin.status === 'Available';
    });
  }

  async function assignOrderBin(orderId) {
    const selectedBin = selectedBinsByOrder[orderId];
    const result = await request('bins_assign', {
      wave_id: waveId,
      order_id: orderId,
      bin_code: selectedBin,
      assignment_method: 'manual',
    });

    if (!result?.success) {
      error = result?.error || 'Failed to assign bin.';
      message = '';
      await refreshWave();
      return;
    }

    error = '';
    message = t('bins.sorter.messages.bin_assigned_order', {
      order: orderId,
      bin: selectedBin,
    });
    await refreshWave();
  }

  function findWaveOrderByScan(scanValue) {
    const normalized = normalizeOrderScan(scanValue);
    if (!normalized || !wave) {
      return null;
    }

    return wave.orders.find((order) => {
      const orderId = normalizeOrderScan(order.order_id);
      const orderName = normalizeOrderScan(order.order_name);
      return orderId === normalized || orderName === normalized;
    }) || null;
  }

  async function assignByScan() {
    const order = findWaveOrderByScan(assignOrderInput);
    if (!order) {
      error = t('bins.sorter.errors.order_not_found');
      message = '';
      return;
    }

    const scannedBin = String(assignBinInput || '').trim();
    const result = await request('bins_assign', {
      wave_id: waveId,
      order_id: order.order_id,
      bin_code: scannedBin,
      assignment_method: 'scan',
    });
    if (!result?.success) {
      error = result?.error || 'Failed to assign bin.';
      message = '';
      await refreshWave();
      return;
    }

    assignOrderInput = '';
    assignBinInput = '';
    error = '';
    message = t('bins.sorter.messages.bin_assigned_order', {
      order: order.order_id,
      bin: scannedBin,
    });
    await refreshWave();

    if (assignOrderInputElement) {
      assignOrderInputElement.focus();
    }
  }

  function onAssignOrderKeydown(event) {
    if (event.key === 'Enter') {
      event.preventDefault();
      if (assignBinInputElement) {
        assignBinInputElement.focus();
      }
    }
  }

  function onAssignBinKeydown(event) {
    if (event.key === 'Enter') {
      event.preventDefault();
      assignByScan();
    }
  }

  function orderProgressRows(order) {
    return Object.keys(order.needs)
      .sort((a, b) => a.localeCompare(b))
      .map((sku) => ({
        sku,
        sorted: Number(order.sorted[sku]) || 0,
        required: Number(order.needs[sku]) || 0,
      }));
  }

  function isOrderAssigned(order) {
    return String(order?.bin_code || '').trim() !== '';
  }

  function orderProducts(order) {
    if (Array.isArray(order?.items_details) && order.items_details.length) {
      return order.items_details;
    }

    return Object.keys(order?.needs || {}).map((sku) => ({
      sku,
      name: '',
      quantity: order.needs[sku],
      barcode: '',
      datalogics_location: itemLocationHints[itemHintKey(order?.order_id, sku)] || '',
      inventory_by_store: getOrderSkuInventory(order?.order_id, sku),
    }));
  }

  function waveProducts() {
    const aggregated = {};

    (wave?.orders || []).forEach((order) => {
      orderProducts(order).forEach((item) => {
        const sku = String(item?.sku || '').trim();
        if (!sku) {
          return;
        }

        if (!aggregated[sku]) {
          aggregated[sku] = {
            sku,
            name: String(item?.name || '').trim(),
            qty: 0,
          };
        }

        aggregated[sku].qty += Number(item?.quantity || 0);
        if (!aggregated[sku].name && item?.name) {
          aggregated[sku].name = String(item.name).trim();
        }
      });
    });

    return Object.keys(aggregated)
      .sort((a, b) => a.localeCompare(b))
      .map((sku) => aggregated[sku]);
  }

  onMount(() => {
    if (!$UserStore || !$UserStore.data) {
      push('/login');
      return;
    }

    if (params?.shipments_ids) {
      if (createFlowStarted) {
        return;
      }

      createFlowStarted = true;
      const tab = params?.tab || '1';
      createWaveFromFetchedOrders(params.shipments_ids, tab);
      return;
    }

    waveId = params?.wave_id || '';
    if (!waveId) {
      push('/bins');
      return;
    }

    refreshWave();
  });

  onDestroy(() => {
    if (completionPopupTimer) {
      clearTimeout(completionPopupTimer);
      completionPopupTimer = null;
    }
  });
</script>

<div class="page-container" transition:fade={{ delay: 250, duration: 300 }}>
  <Preloader {isLoading} />
  <Header activeLink="shipments" />
  <div class="page">
    <TopInfo title={$_('bins.sorter.title')} />
    <div class="shipment">

    {#if !wave}
      <div class="bg-white rounded-xl shadow p-4">
        <div class="text-red-700 mb-2">{$_('bins.sorter.wave_not_found')}</div>
        <a href="/bins" use:link class="bg-slate-200 rounded px-3 py-1 inline-block">{$_('bins.sorter.back_to_bins')}</a>
      </div>
    {:else}
      <div class="bg-white rounded-xl shadow p-4 mb-4">
        <div class="flex items-start justify-between gap-3">
          <div>
            <div class="font-bold">{wave.wave_id}</div>
            <div class="text-sm text-slate-600">
              {$_('bins.sorter.wave_meta', { values: { status: wave.status, type: wave.type, orders: wave.orders.length } })}
            </div>
          </div>
          <button on:click={refreshInventoryNow} class="bg-slate-200 hover:bg-slate-300 rounded px-3 py-1 text-sm whitespace-nowrap" disabled={isSyncingInventory}>
            {isSyncingInventory ? $_('bins.sorter.refreshing_inventory') : $_('bins.sorter.refresh_inventory')}
          </button>
        </div>
      </div>

      <div class="bg-white rounded-xl shadow p-4 mb-4">
        <div class="font-bold mb-2">{$_('bins.sorter.quick_assign_title')}</div>
        <div class="grid md:grid-cols-3 gap-3 items-end">
          <div>
            <label class="block text-sm mb-1" for="assignOrderInput">{$_('bins.sorter.scan_order')}</label>
            <input
              id="assignOrderInput"
              bind:value={assignOrderInput}
              bind:this={assignOrderInputElement}
              on:keydown={onAssignOrderKeydown}
              class="border rounded px-2 py-1 w-full"
              placeholder={$_('bins.sorter.scan_order_placeholder')}
            />
          </div>
          <div>
            <label class="block text-sm mb-1" for="assignBinInput">{$_('bins.sorter.scan_bin_for_assignment')}</label>
            <input
              id="assignBinInput"
              bind:value={assignBinInput}
              bind:this={assignBinInputElement}
              on:keydown={onAssignBinKeydown}
              class="border rounded px-2 py-1 w-full"
              placeholder={$_('bins.sorter.scan_bin_placeholder')}
            />
          </div>
          <div>
            <button on:click={assignByScan} class="bg-red-600 text-white rounded px-3 py-2 w-full">
              {$_('bins.sorter.assign_scanned')}
            </button>
          </div>
        </div>
      </div>

      <div class="bg-white rounded-xl shadow p-4 mb-4">
        <div class="font-bold mb-2">{$_('bins.sorter.scan_flow')}</div>
        <div class="grid md:grid-cols-2 gap-3">
          <div>
            <label class="block text-sm mb-1" for="skuInput">{$_('bins.sorter.step_sku')}</label>
            <div class="flex gap-2">
              <input id="skuInput" bind:value={skuInput} class="border rounded px-2 py-1 w-full" placeholder={$_('bins.sorter.scan_sku_placeholder')} />
              <button on:click={scanSku} class="bg-red-600 text-white rounded px-3 py-1">{$_('bins.sorter.suggest')}</button>
            </div>
          </div>

          <div>
            <label class="block text-sm mb-1" for="binInput">{$_('bins.sorter.step_bin')}</label>
            <input id="binInput" bind:value={binInput} class="border rounded px-2 py-1 w-full" placeholder={$_('bins.sorter.scan_bin_placeholder')} />
          </div>
        </div>

        {#if suggestion}
          <div class="mt-3 p-3 bg-slate-50 rounded border">
            <div class="text-sm">{$_('bins.sorter.suggested_order')}: <b>{suggestion.order_id}</b></div>
            <div class="text-sm">{$_('bins.sorter.suggested_bin')}: <b>{suggestion.bin_code || $_('bins.sorter.new_bin_required')}</b></div>

            <div class="mt-2">
              <label class="block text-sm mb-1" for="overrideTarget">{$_('bins.sorter.override_target')}</label>
              <select id="overrideTarget" bind:value={selectedOrderId} class="border rounded px-2 py-1 w-full">
                {#each candidates as order}
                  <option value={order.order_id}>{order.order_id}</option>
                {/each}
              </select>
            </div>

            {#if selectedOrderId && selectedOrderId !== suggestion.order_id}
              <div class="mt-2">
                <label class="block text-sm mb-1" for="overrideReason">{$_('bins.sorter.override_reason')}</label>
                <input id="overrideReason" bind:value={overrideReason} class="border rounded px-2 py-1 w-full" placeholder={$_('bins.sorter.override_reason_placeholder')} />
              </div>
            {/if}

            <div class="mt-3 flex gap-2">
              <button on:click={commitSort} class="bg-emerald-600 text-white rounded px-3 py-1">{$_('bins.sorter.confirm_sort')}</button>
              {#if wave.status !== WAVE_STATUS.CLOSED}
                <button on:click={closeCurrentWave} class="bg-amber-500 text-white rounded px-3 py-1">{$_('bins.sorter.close_wave')}</button>
              {/if}
            </div>
          </div>
        {/if}

        {#if externalOpenOrderCandidates.length}
          <div class="mt-3 p-3 rounded border border-amber-300 bg-amber-50">
            <div class="font-semibold text-sm mb-2">Open orders outside this wave</div>
            <div class="text-xs text-slate-700 mb-3">
              This item is also needed by open orders from previous waves. Select orders to move into this wave.
            </div>

            <div class="space-y-2 mb-3 max-h-52 overflow-auto">
              {#each externalOpenOrderCandidates as candidate}
                <label class="flex items-start gap-2 text-xs bg-white rounded border px-2 py-2">
                  <input
                    type="checkbox"
                    checked={Boolean(selectedExternalOrders[externalOrderKey(candidate)])}
                    on:change={(event) => toggleExternalOrderSelection(candidate, event.currentTarget.checked)}
                  />
                  <span>
                    <b>{candidate.order_id}</b>
                    <span class="text-slate-500"> ({candidate.source_wave_id})</span>
                    {#if candidate.order_name}
                      <span class="text-slate-600"> — {candidate.order_name}</span>
                    {/if}
                    <br />
                    <span class="text-slate-600">SKU: {candidate.sku} | Remaining: {candidate.remaining_qty}</span>
                    {#if candidate.bin_code}
                      <span class="text-slate-600"> | Bin: {candidate.bin_code}</span>
                    {/if}
                  </span>
                </label>
              {/each}
            </div>

            <div class="flex gap-2">
              <button on:click={addSelectedOpenOrdersToCurrentWave} class="bg-amber-600 text-white rounded px-3 py-1 text-sm" disabled={isAddingExternalOrders}>
                {isAddingExternalOrders ? 'Adding...' : 'Add selected orders to this wave'}
              </button>
              <button
                on:click={() => {
                  externalOpenOrderCandidates = [];
                  selectedExternalOrders = {};
                }}
                class="bg-slate-200 rounded px-3 py-1 text-sm"
              >
                Dismiss
              </button>
            </div>
          </div>
        {/if}

        {#if message}
          <div class="mt-3 text-emerald-700">{message}</div>
        {/if}
        {#if error}
          <div class="mt-3 text-red-700">{error}</div>
        {/if}

        {#if inventoryMoveState.visible}
          <div class="mt-3 p-3 rounded border border-amber-300 bg-amber-50">
            <div class="font-semibold text-sm mb-2">{$_('bins.sorter.inventory_move_title')}</div>
            <div class="text-xs text-slate-700 mb-3">{$_('bins.sorter.inventory_move_desc')}</div>

            <div class="grid md:grid-cols-4 gap-2 items-end">
              <div>
                <label class="block text-xs mb-1" for="inventoryMoveFrom">{$_('bins.sorter.inventory_move_from')}</label>
                <select id="inventoryMoveFrom" bind:value={inventoryMoveState.fromLocation} class="border rounded px-2 py-1 w-full text-sm">
                  {#each inventoryMoveState.sourceOptions as source}
                    <option value={source.location}>{source.location} ({source.quantity})</option>
                  {/each}
                </select>
              </div>

              <div>
                <label class="block text-xs mb-1" for="inventoryMoveTo">{$_('bins.sorter.inventory_move_to')}</label>
                <select id="inventoryMoveTo" bind:value={inventoryMoveState.toLocation} class="border rounded px-2 py-1 w-full text-sm">
                  {#each inventoryMoveState.targetOptions as target}
                    <option value={target}>{target}</option>
                  {/each}
                </select>
              </div>

              <div>
                <label class="block text-xs mb-1" for="inventoryMoveQty">{$_('bins.sorter.inventory_move_qty')}</label>
                <input id="inventoryMoveQty" type="number" min="1" bind:value={inventoryMoveState.quantity} class="border rounded px-2 py-1 w-full text-sm" />
              </div>

              <div class="flex gap-2">
                <button on:click={moveInventoryBetweenWarehouses} class="bg-amber-600 text-white rounded px-3 py-1 text-sm" disabled={isMovingInventory}>
                  {isMovingInventory ? $_('bins.sorter.inventory_move_working') : $_('bins.sorter.inventory_move_action')}
                </button>
                <button on:click={hideInventoryMovePrompt} class="bg-slate-200 rounded px-3 py-1 text-sm">{$_('bins.common.close')}</button>
              </div>
            </div>
          </div>
        {/if}
      </div>

      <div class="bg-white rounded-xl shadow p-4 mb-4">
        <div class="font-bold mb-2">{$_('bins.sorter.orders_in_wave')}</div>
        <div class="overflow-auto">
          <table class="w-full text-sm">
            <thead>
              <tr class="border-b">
                <th class="text-left py-2">{$_('bins.sorter.order')}</th>
                <th class="text-left py-2">{$_('bins.sorter.bin')}</th>
                <th class="text-left py-2">{$_('bins.sorter.assign_bin')}</th>
                <th class="text-left py-2">{$_('bins.sorter.status')}</th>
                <th class="text-left py-2">{$_('bins.sorter.products')}</th>
                <th class="text-left py-2">{$_('bins.sorter.progress')}</th>
              </tr>
            </thead>
            <tbody>
              {#each wave.orders as order}
                <tr class={`border-b ${isOrderFullyScanned(order) ? 'bg-emerald-50/60' : ''}`}>
                  <td class="py-2">
                    <div>{order.order_id}</div>
                    {#if order.order_name}
                      <div class="text-slate-500 text-xs flex items-center gap-2">
                        <span>{order.order_name}</span>
                        {#if isOrderAssigned(order)}
                          <button on:click={() => releaseOrderBin(order)} class="bg-slate-800 text-white rounded px-2 py-0.5 text-[10px] whitespace-nowrap">
                            {$_('bins.sorter.release_bin')}
                          </button>
                        {/if}
                      </div>
                    {/if}
                  </td>
                  <td class="py-2">{order.bin_code || '-'}</td>
                  <td class="py-2 min-w-[260px]">
                    <div class="flex gap-2 items-center">
                      <select bind:value={selectedBinsByOrder[order.order_id]} class="border rounded px-2 py-1 w-full">
                        <option value="">{$_('bins.sorter.select_bin')}</option>
                        {#each availableBinsForOrder(order) as bin}
                          <option value={bin.bin_code}>#{bin.label_number} - {bin.bin_code}</option>
                        {/each}
                      </select>
                      <button on:click={() => assignOrderBin(order.order_id)} class="bg-red-600 text-white rounded px-3 py-1 whitespace-nowrap">
                        {$_('bins.sorter.assign')}
                      </button>
                    </div>
                  </td>
                  <td class="py-2">
                    <div>{order.sort_status}</div>
                    {#if isOrderAssigned(order)}
                      <span class="mt-1 inline-flex items-center rounded-full bg-emerald-100 text-emerald-800 px-2 py-0.5 text-[11px] font-semibold">
                        {$_('bins.sorter.badges.assignment_done')}
                      </span>

                      {#if isOrderFullyScanned(order)}
                        <div class="mt-2">
                          <button on:click={() => releaseOrderBin(order)} class="bg-slate-800 text-white rounded px-2 py-1 text-xs">
                            {$_('bins.sorter.release_bin')}
                          </button>
                        </div>
                      {/if}
                    {/if}
                  </td>
                  <td class="py-2">
                    <div class="space-y-2">
                      {#each orderProducts(order) as item}
                        <div class={`${isItemOutOfStockInConfiguredLocations(order.order_id, item) ? 'bg-red-100 border border-red-300 text-red-800' : 'bg-slate-100'} rounded px-3 py-2 text-xs min-w-[170px]`}>
                          {#if isItemOutOfStockInConfiguredLocations(order.order_id, item)}
                            <div class="text-[11px] font-bold uppercase tracking-wide text-red-700">{$_('bins.sorter.no_inventory_badge')}</div>
                          {/if}
                          {#if item.name}
                            <div class="font-semibold leading-tight">{item.name}</div>
                          {/if}
                          <div><b>{item.sku}</b> × {item.quantity}</div>
                          {#if item.barcode}
                            <div class="text-slate-500">{item.barcode}</div>
                          {/if}
                          {#if item.datalogics_location}
                            <div class="text-amber-700 font-semibold">{$_('bins.sorter.location_label')}: {item.datalogics_location}</div>
                          {/if}
                          <div class="mt-1">
                            <button on:click={() => openInventoryPopup(order.order_id, item)} class="text-xs bg-slate-200 hover:bg-slate-300 rounded px-2 py-1">
                              {$_('bins.sorter.inventory_details')}
                            </button>
                          </div>
                        </div>
                      {/each}
                    </div>
                  </td>
                  <td class="py-2">
                    <div class="space-y-1 text-xs">
                      {#each orderProgressRows(order) as row}
                        <div>{row.sku}: {row.sorted}/{row.required}</div>
                      {/each}
                    </div>
                  </td>
                </tr>
              {/each}
            </tbody>
          </table>
        </div>
      </div>

      <div class="bg-white rounded-xl shadow p-4">
        <div class="font-bold mb-2">{$_('bins.sorter.recent_events')}</div>
        {#if !events.length}
          <div class="text-slate-500 text-sm">{$_('bins.sorter.no_events')}</div>
        {:else}
          <div class="space-y-1 text-sm">
            {#each events as ev}
              <div class="border-b pb-1">
                [{ev.time}] {ev.action} | {$_('bins.sorter.sku')}: {ev.sku} | {$_('bins.sorter.order')}: {ev.order_id} | {$_('bins.sorter.bin')}: {ev.bin_code} {ev.reason ? `| ${$_('bins.sorter.reason')}: ${ev.reason}` : ''}
              </div>
            {/each}
          </div>
        {/if}
      </div>
    {/if}
    </div>
  </div>
</div>

{#if inventoryPopup.open && inventoryPopup.item}
  <div
    class="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4"
    on:click={closeInventoryPopup}
    on:keydown={onInventoryOverlayKeydown}
    role="button"
    tabindex="0"
  >
    <div class="bg-white rounded-xl shadow p-4 w-full max-w-md" on:mousedown|stopPropagation>
      <div class="flex items-start justify-between gap-3 mb-3">
        <div>
          <div class="font-bold">{$_('bins.sorter.inventory_popup_title')}</div>
          <div class="text-sm text-slate-600">
            {inventoryPopup.item?.sku || ''}
            {#if inventoryPopup.item?.name}
              - {inventoryPopup.item.name}
            {/if}
          </div>
        </div>
        <button on:click={closeInventoryPopup} class="bg-slate-200 hover:bg-slate-300 rounded px-2 py-1 text-sm">{$_('bins.common.close')}</button>
      </div>

      {#if itemInventoryRows(inventoryPopup.orderId, inventoryPopup.item).length}
        <div class="space-y-1 text-sm">
          {#each itemInventoryRows(inventoryPopup.orderId, inventoryPopup.item) as row}
            <div class="flex items-center justify-between border rounded px-2 py-1">
              <div>{$_('bins.sorter.location_label')}: <b>{row.location}</b></div>
              <div>{$_('bins.sorter.quantity_label')}: <b>{row.quantity}</b></div>
            </div>
          {/each}
        </div>
      {:else}
        <div class="text-sm text-slate-500">{$_('bins.sorter.no_inventory_data')}</div>
      {/if}
    </div>
  </div>
{/if}

{#if completionPopup.open}
  <div class="fixed top-4 right-4 z-50 w-full max-w-sm">
    <div class="bg-emerald-600 text-white rounded-xl shadow-lg p-4">
      <div class="font-semibold mb-1">Order completed</div>
      <div class="text-sm mb-3">Order <b>{completionPopup.orderId}</b> is fully scanned.</div>
      <div class="flex justify-end">
        <button on:click={closeCompletionPopup} class="bg-white/20 hover:bg-white/30 rounded px-3 py-1 text-sm">Close</button>
      </div>
    </div>
  </div>
{/if}
